/**
 * Somebody is calling you.
 *
 * A call in a conversation used to be a line in the message log and nothing
 * else: the person who started it sat in an empty room while the other end
 * carried on unaware. The server now rings whoever is not yet in the room, and
 * this is what a ring looks like - a card that follows you across every screen
 * until it is answered, turned down, or gives up on its own.
 *
 * Deliberately not a modal. A ring should not take the app hostage: you can
 * keep reading, keep typing, and answer when you are ready.
 */

import { useEffect, useRef, useState } from 'react';
import { socket } from '@/lib/api';
import { play } from '@/lib/sound';
import { useVoice } from '@/stores/voice';
import { go } from '@/lib/hash';
import { Avatar } from './Avatar';
import { useToast } from './ui';
import { Icon } from '@/icons/Icon';
import type { PublicUser } from '@/lib/types';

type Ring = {
  room: string;
  kind: 'dm' | 'group';
  /** The group's name, or null when it is a person calling. */
  name: string | null;
  from: PublicUser;
  expiresAt: number;
};

/** How often the tone repeats while a card is up. */
const RING_EVERY_MS = 2600;

export function IncomingCallHost() {
  const [rings, setRings] = useState<Ring[]>([]);
  const voice = useVoice();
  const toast = useToast();

  useEffect(() => {
    const offs = [
      socket.on('call:ring', (d: unknown) => {
        const ring = d as Ring;
        if (!ring?.room || !ring.from) return;
        setRings((list) => [...list.filter((r) => r.room !== ring.room), ring]);
      }),
      /* Answered elsewhere, hung up, or turned down - all the same to this
         screen: the card goes away without a sound. */
      socket.on('call:cancelled', (d: unknown) => {
        const room = (d as { room?: string })?.room;
        setRings((list) => list.filter((r) => r.room !== room));
      }),
      socket.on('call:missed', (d: unknown) => {
        const room = (d as { room?: string })?.room;
        setRings((list) => list.filter((r) => r.room !== room));
      })
    ];
    return () => { for (const off of offs) off(); };
  }, []);

  /*
   * The other half of a ring: being turned down.
   *
   * The caller is left in a room that will never fill, so they are told
   * rather than left to work it out from silence.
   */
  useEffect(() => socket.on('call:declined', () => toast('Call declined', 'bad')), [toast]);

  /* A ring you have already joined is not a ring. Answering from the header
     rather than the card should still take the card down. */
  useEffect(() => {
    if (!voice.partyId) return;
    setRings((list) => list.filter((r) => r.room !== voice.partyId));
  }, [voice.partyId]);

  /*
   * The tone, on a loop.
   *
   * One interval for the whole stack rather than one per card, so three
   * people calling at once is still a ring rather than a chord.
   */
  const ringing = rings.length > 0;
  useEffect(() => {
    if (!ringing) return;
    play('ring');
    const t = setInterval(() => play('ring'), RING_EVERY_MS);
    return () => clearInterval(t);
  }, [ringing]);

  /* Cards expire on their own, in case the cancel never lands. */
  useEffect(() => {
    if (!ringing) return;
    const t = setInterval(() => {
      setRings((list) => list.filter((r) => r.expiresAt > Date.now()));
    }, 1000);
    return () => clearInterval(t);
  }, [ringing]);

  if (!rings.length) return null;

  return (
    <div className="pointer-events-none fixed right-4 top-16 z-[130] flex w-[min(340px,calc(100vw-2rem))] flex-col gap-2">
      {rings.map((ring) => (
        <RingCard
          key={ring.room}
          ring={ring}
          onAnswer={() => {
            setRings((list) => list.filter((r) => r.room !== ring.room));
            void useVoice.getState().join(ring.room);
            go(ring.kind === 'group' ? 'chat/g/' + ring.room : 'chat/' + ring.from.username);
          }}
          onDecline={() => {
            setRings((list) => list.filter((r) => r.room !== ring.room));
            socket.send('call:decline', { room: ring.room });
          }}
        />
      ))}
    </div>
  );
}

function RingCard({ ring, onAnswer, onDecline }: {
  ring: Ring;
  onAnswer: () => void;
  onDecline: () => void;
}) {
  /* Mounted-then-shown, so the card slides in rather than appearing. */
  const [shown, setShown] = useState(false);
  const raf = useRef(0);
  useEffect(() => {
    raf.current = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf.current);
  }, []);

  const who = ring.kind === 'group' ? (ring.name ?? 'Group') : ring.from.displayName;
  const what = ring.kind === 'group'
    ? ring.from.displayName + ' started a call'
    : 'Incoming call';

  return (
    <div
      className={
        'pointer-events-auto flex items-center gap-3 rounded-[var(--radius)] border border-[color-mix(in_srgb,var(--voice)_34%,var(--line))] bg-bg-2 p-3 shadow-[0_18px_44px_-18px_rgba(0,0,0,.75)] transition-[background-color,border-color,box-shadow,transform,opacity] duration-200 ' +
        (shown ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0')
      }
    >
      {/* The pulse is the only thing on screen that says "now". */}
      <span className="relative flex-none">
        <span className="absolute inset-0 animate-ping rounded-full bg-[color-mix(in_srgb,var(--voice)_45%,transparent)]" />
        <span className="relative block rounded-full ring-2 ring-[color-mix(in_srgb,var(--voice)_55%,transparent)]">
          <Avatar user={ring.from} size={40} />
        </span>
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-bold">{who}</span>
        <span className="block truncate text-[11.5px] text-mute">{what}</span>
      </span>

      <button
        type="button"
        onClick={onDecline}
        title="Decline"
        aria-label="Decline"
        className="grid h-9 w-9 flex-none place-items-center rounded-[var(--radius)] bg-[color-mix(in_srgb,var(--bad)_20%,transparent)] text-bad transition-colors hover:bg-[color-mix(in_srgb,var(--bad)_34%,transparent)]"
      >
        <Icon name="callEnd" size={16} />
      </button>

      <button
        type="button"
        onClick={onAnswer}
        title="Answer"
        aria-label="Answer"
        className="grid h-9 w-9 flex-none place-items-center rounded-[var(--radius)] bg-[color-mix(in_srgb,var(--voice)_22%,transparent)] text-voice transition-colors hover:bg-[color-mix(in_srgb,var(--voice)_36%,transparent)]"
      >
        <Icon name="call" size={16} />
      </button>
    </div>
  );
}
